import React, { useState, useCallback } from 'react';
import {
  Modal,
  ModalHeader,
  ModalBody,
  ModalFooter,
  Button,
  Select,
  Textarea,
  Badge,
} from '@mellify/ui';
import { UserPlus, Mail, CheckCircle2, AlertCircle, Clock, X } from 'lucide-react';
import { useWorkspaceStore } from '../../../../stores/workspaceStore';
import { useInviteMembers } from '../hooks/useInviteMembers';
import type { InviteMembersResponse, InvitationResult } from '../../../../services/workspaceService';
import type { WorkspaceMemberRole } from '@mellify/shared';

interface InviteMemberModalProps {
  open: boolean;
  onClose: () => void;
}

const ROLE_OPTIONS = [
  {
    value: 'admin',
    label: 'Admin',
    description: 'Full workspace management, can invite members and manage settings.',
  },
  {
    value: 'manager',
    label: 'Manager',
    description: 'Manage campaigns, content, and publishing schedules.',
  },
  {
    value: 'approver',
    label: 'Approver',
    description: 'Review and approve content before publishing.',
  },
  {
    value: 'analyst',
    label: 'Analyst',
    description: 'View analytics, reports, and performance data.',
  },
  {
    value: 'support',
    label: 'Support',
    description: 'Handle engagement inbox and respond to messages.',
  },
];

const STATUS_ICON_MAP: Record<InvitationResult['status'], React.ReactNode> = {
  invited: <CheckCircle2 className="w-4 h-4 text-success-500" />,
  already_member: <AlertCircle className="w-4 h-4 text-warning-500" />,
  already_invited: <Clock className="w-4 h-4 text-info-500" />,
  error: <AlertCircle className="w-4 h-4 text-danger-500" />,
};

const STATUS_BADGE_VARIANT: Record<InvitationResult['status'], 'success' | 'warning' | 'info' | 'danger'> = {
  invited: 'success',
  already_member: 'warning',
  already_invited: 'info',
  error: 'danger',
};

const STATUS_LABEL: Record<InvitationResult['status'], string> = {
  invited: 'Invited',
  already_member: 'Already Member',
  already_invited: 'Already Invited',
  error: 'Error',
};

export const InviteMemberModal: React.FC<InviteMemberModalProps> = ({ open, onClose }) => {
  const currentWorkspace = useWorkspaceStore((s) => s.currentWorkspace);

  // Form state
  const [emailsInput, setEmailsInput] = useState('');
  const [selectedRole, setSelectedRole] = useState<string>('manager');
  const [personalMessage, setPersonalMessage] = useState('');

  // Result state
  const [inviteResults, setInviteResults] = useState<InviteMembersResponse | null>(null);

  const handleSuccess = useCallback((data: InviteMembersResponse) => {
    setInviteResults(data);
  }, []);

  const { mutate: inviteMembers, isPending } = useInviteMembers({
    workspaceId: currentWorkspace?.id || null,
    onSuccess: handleSuccess,
  });

  // Parse emails from input
  const parseEmails = (input: string): string[] => {
    return input
      .split(/[,;\n]+/)
      .map((e) => e.trim().toLowerCase())
      .filter((e) => e.length > 0);
  };

  const parsedEmails = parseEmails(emailsInput);
  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const validEmails = parsedEmails.filter((e) => emailRegex.test(e));
  const invalidEmails = parsedEmails.filter((e) => !emailRegex.test(e));

  const handleSubmit = () => {
    if (validEmails.length === 0) return;

    inviteMembers({
      emails: validEmails,
      role: selectedRole as WorkspaceMemberRole,
      personalMessage: personalMessage.trim() || undefined,
    });
  };

  const handleClose = () => {
    // Reset form
    setEmailsInput('');
    setSelectedRole('manager');
    setPersonalMessage('');
    setInviteResults(null);
    onClose();
  };

  const handleInviteMore = () => {
    setEmailsInput('');
    setPersonalMessage('');
    setInviteResults(null);
  };

  const selectedRoleInfo = ROLE_OPTIONS.find((r) => r.value === selectedRole);

  // Show results view
  if (inviteResults) {
    return (
      <Modal
        open={open}
        onClose={handleClose}
        size="md"
        aria-label="Invitation Results"
      >
        <ModalHeader>
          <div className="flex items-center gap-2">
            <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
              <Mail className="w-4 h-4 text-primary-600 dark:text-primary-400" />
            </div>
            <div>
              <h2 className="text-lg font-semibold text-dark-900 dark:text-dark-100">
                Invitation Results
              </h2>
              <p className="text-xs text-dark-500 dark:text-dark-400">
                {inviteResults.invitedCount} invited, {inviteResults.skippedCount} skipped
              </p>
            </div>
          </div>
        </ModalHeader>

        <ModalBody>
          {/* Success summary */}
          {inviteResults.invitedCount > 0 && (
            <div className="mb-4 p-3 rounded-lg bg-success-50 dark:bg-success-900/20 border border-success-200 dark:border-success-800">
              <div className="flex items-center gap-2">
                <CheckCircle2 className="w-5 h-5 text-success-600 dark:text-success-400 shrink-0" />
                <p className="text-sm font-medium text-success-800 dark:text-success-300">
                  {inviteResults.invitedCount} invitation{inviteResults.invitedCount !== 1 ? 's' : ''} sent successfully!
                </p>
              </div>
            </div>
          )}

          {/* Results list */}
          <div className="space-y-2">
            {inviteResults.results.map((result, index) => (
              <div
                key={`${result.email}-${index}`}
                className="flex items-center justify-between px-3 py-2 rounded-lg bg-dark-50 dark:bg-dark-800/50"
              >
                <div className="flex items-center gap-3 min-w-0">
                  {STATUS_ICON_MAP[result.status]}
                  <span className="text-sm text-dark-700 dark:text-dark-200 truncate">
                    {result.email}
                  </span>
                </div>
                <Badge
                  variant={STATUS_BADGE_VARIANT[result.status]}
                  size="sm"
                >
                  {STATUS_LABEL[result.status]}
                </Badge>
              </div>
            ))}
          </div>
        </ModalBody>

        <ModalFooter>
          <Button variant="outline" size="md" onClick={handleInviteMore}>
            Invite More
          </Button>
          <Button variant="primary" size="md" onClick={handleClose}>
            Done
          </Button>
        </ModalFooter>
      </Modal>
    );
  }

  // Show invite form
  return (
    <Modal
      open={open}
      onClose={handleClose}
      size="md"
      aria-label="Invite Team Members"
    >
      <ModalHeader>
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-full bg-primary-100 dark:bg-primary-900/30 flex items-center justify-center">
            <UserPlus className="w-4 h-4 text-primary-600 dark:text-primary-400" />
          </div>
          <div>
            <h2 className="text-lg font-semibold text-dark-900 dark:text-dark-100">
              Invite Team Members
            </h2>
            <p className="text-xs text-dark-500 dark:text-dark-400">
              Invite people to join {currentWorkspace?.name || 'your workspace'}
            </p>
          </div>
        </div>
      </ModalHeader>

      <ModalBody>
        <div className="space-y-5">
          {/* Email input */}
          <div>
            <Textarea
              label="Email Addresses"
              placeholder="Enter email addresses separated by commas or new lines..."
              value={emailsInput}
              onChange={(e) => setEmailsInput(e.target.value)}
              helperText={
                parsedEmails.length > 0
                  ? `${validEmails.length} valid email${validEmails.length !== 1 ? 's' : ''}${invalidEmails.length > 0 ? `, ${invalidEmails.length} invalid` : ''}`
                  : 'Separate multiple emails with commas, semicolons, or new lines.'
              }
              error={
                parsedEmails.length > 0 && invalidEmails.length > 0
                  ? `Invalid: ${invalidEmails.join(', ')}`
                  : undefined
              }
              rows={3}
              size="md"
            />
            {/* Email chips preview */}
            {validEmails.length > 0 && (
              <div className="flex flex-wrap gap-1.5 mt-2">
                {validEmails.map((email) => (
                  <span
                    key={email}
                    className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full bg-primary-50 dark:bg-primary-900/20 text-xs text-primary-700 dark:text-primary-300 border border-primary-200 dark:border-primary-800"
                  >
                    <Mail className="w-3 h-3" />
                    {email}
                    <button
                      type="button"
                      onClick={() => {
                        const remaining = parsedEmails.filter((e) => e !== email);
                        setEmailsInput(remaining.join(', '));
                      }}
                      className="ml-0.5 text-primary-400 hover:text-primary-600 dark:text-primary-500 dark:hover:text-primary-300"
                      aria-label={`Remove ${email}`}
                    >
                      <X className="w-3 h-3" />
                    </button>
                  </span>
                ))}
              </div>
            )}
          </div>

          {/* Role selector */}
          <div>
            <Select
              label="Role"
              value={selectedRole}
              onChange={(e) => setSelectedRole(e.target.value)}
              options={ROLE_OPTIONS.map((r) => ({ value: r.value, label: r.label }))}
              size="md"
            />
            {selectedRoleInfo && (
              <p className="mt-1.5 text-xs text-dark-500 dark:text-dark-400">
                {selectedRoleInfo.description}
              </p>
            )}
          </div>

          {/* Personal message */}
          <Textarea
            label="Personal Message (Optional)"
            placeholder="Add a personal note to your invitation..."
            value={personalMessage}
            onChange={(e) => setPersonalMessage(e.target.value)}
            maxLength={500}
            showCount
            rows={3}
            size="md"
          />
        </div>
      </ModalBody>

      <ModalFooter>
        <Button
          variant="outline"
          size="md"
          onClick={handleClose}
          disabled={isPending}
        >
          Cancel
        </Button>
        <Button
          variant="primary"
          size="md"
          onClick={handleSubmit}
          loading={isPending}
          disabled={validEmails.length === 0 || isPending}
          leftIcon={<UserPlus className="w-4 h-4" />}
        >
          {validEmails.length > 0
            ? `Invite ${validEmails.length} Member${validEmails.length !== 1 ? 's' : ''}`
            : 'Invite Members'}
        </Button>
      </ModalFooter>
    </Modal>
  );
};

InviteMemberModal.displayName = 'InviteMemberModal';